/* eslint-disable no-plusplus */
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {
  AfterViewInit,
  AfterContentInit,
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ContentChild,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  Attribute,
  ContentChildren,
  QueryList,
  booleanAttribute,
  numberAttribute,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FocusMonitor } from '@angular/cdk/a11y';
import { EMPTY, merge, Subject } from 'rxjs';
import { concatMap, take, takeUntil, tap } from 'rxjs/operators';
import { Nullable } from '@core/types/nullable.type';
import { ChipActionDirective } from '../../directives/chip-action.directive';
import { CHIP, CHIP_REMOVE } from '../../variables/chip.variable';
import { ChipRemoveDirective } from '../../directives/chip-remove.directive';

let uid = 0;

/** Represents an event fired on an individual `core-chip`. */
export interface ChipEvent {
  /** The chip the event was fired on. */
  chip: ChipBaseComponent;
}

/**
 * Chip base component. Used inside the ChipGroupBase component.
 *
 * Extended by ChipOption and ChipEnteredInputComponent for different interaction patterns.
 */
@Component({
  selector: 'core-chip-base, [coreChipBase]',
  exportAs: 'coreChipBase',
  templateUrl: './chip-base.component.html',
  styleUrls: ['./chip-base.component.scss'],
  host: {
    '[class]': '"chip"',
    '[class.chip--selectable]': '_selectable',
    '[class.chip--selected]': '_selected',
    '[class.chip--highlighted]': 'highlighted',
    '[class.chip--disabled]': 'disabled',
    '[class.chip--multiline]': '!!title',
    '[class.chip--avatar]': '!!avatarSrc',
    '[id]': 'id',
    '[attr.role]': 'role',
    '[attr.tabindex]': 'getTabIndex()',
    '[attr.aria-label]': 'ariaLabel',
    '(keydown)': 'handleKeydown($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: CHIP, useExisting: ChipBaseComponent }],
})
export class ChipBaseComponent implements AfterViewInit, AfterContentInit, OnDestroy {
  @Input()
  icon?: string;

  @Input()
  avatarSrc?: string;

  /**
   * Optional title for the chip, displayed over the main text
   */
  @Input()
  title?: string;

  /**
   * Overwrite the default title opacity of 50%
   */
  @Input()
  titleOpacity?: number;

  protected _selectable = false;

  protected _selected = false;

  protected document: Document;

  /**
   * Emits when the chip is focused
   */
  readonly onFocus$ = new Subject<ChipEvent>();

  /**
   * Emits when the chip is blurred
   */
  readonly onBlur$ = new Subject<ChipEvent>();

  /**
   * Role for the root of the chip
   */
  @Input()
  role: Nullable<string> = null;

  /**
   * Whether the chip has focus
   */
  private hasFocusInternal = false;

  /**
   * Whether moving focus into the chip is pending
   */
  private pendingFocus!: boolean;

  /** All remove icons present in the chip. */
  @ContentChildren(CHIP_REMOVE, { descendants: true })
  protected allRemoveIcons!: QueryList<ChipRemoveDirective>;

  hasFocus(): boolean {
    return this.hasFocusInternal;
  }

  /**
   * A unique id for the chip. If none is supplied, it will be auto-generated
   */
  @Input()
  id = `core-chip-${uid++}`;

  /**
   * ARIA label for the content of the chip
   */
  @Input('aria-label')
  ariaLabel: Nullable<string> = null;

  /**
   * ARIA description for the content of the chip
   */
  @Input('aria-description')
  ariaDescription: Nullable<string> = null;

  private textElement!: HTMLElement;

  /**
   * The value of the chip. Defaults to the content inside the label
   */
  @Input()
  get value(): any {
    return this._value !== undefined ? this._value : this.textElement.textContent!.trim();
  }

  set value(value: any) {
    this._value = value;
  }

  protected _value: any;

  /**
   * Determines whether or not the chip displays the remove styling and emits (removed) events
   */
  @Input({ transform: booleanAttribute })
  removable: boolean = true;

  /**
   * Colors the chip for emphasis as if it were selected
   */
  @Input({ transform: booleanAttribute })
  highlighted: boolean = false;

  /**
   * Whether the chip is disabled
   */
  @Input({ transform: booleanAttribute })
  disabled: boolean = false;

  /**
   * Tab index of the chip
   */
  @Input({
    transform: (value: unknown) => (value == null ? undefined : numberAttribute(value)),
  })
  tabIndex: number = -1;

  /**
   * Emitted when a chip is to be removed
   */
  @Output()
  readonly removed: EventEmitter<ChipEvent> = new EventEmitter<ChipEvent>();

  /**
   * Emitted when the chip is destroyed
   */
  @Output()
  readonly destroyed: EventEmitter<ChipEvent> = new EventEmitter<ChipEvent>();

  /**
   * The chip's trailing remove icon
   */
  @ContentChild(CHIP_REMOVE)
  removeIcon!: ChipRemoveDirective;

  /**
   * Action receiving the primary set of user interactions
   */
  @ViewChild(ChipActionDirective)
  primaryAction!: ChipActionDirective;

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef<HTMLElement>,
    protected ngZone: NgZone,
    private focusMonitor: FocusMonitor,
    @Inject(DOCUMENT) document: any,
    @Attribute('tabindex')
    tabIndex?: string,
  ) {
    this.document = document;
    if (tabIndex != null) {
      this.tabIndex = parseInt(tabIndex, 10) ?? -1;
    }
    this.monitorFocus();
  }

  ngAfterViewInit(): void {
    this.textElement = this.elementRef.nativeElement.querySelector('.chip-label')!;

    if (this.pendingFocus) {
      this.pendingFocus = false;
      this.focus();
    }
  }

  ngAfterContentInit(): void {
    // Since the styling depends on the presence of some
    // actions, we have to mark for check on changes.
    merge(this.allRemoveIcons.changes)
      .pipe(takeUntil(this.destroyed.asObservable()))
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  /**
   * Allows for programmatic removal of the chip.
   *
   * Informs any listeners of the removal request. Does not remove the chip from the DOM.
   */
  remove(): void {
    if (this.removable) {
      this.removed.emit({ chip: this });
    }
  }

  /**
   * Handles keyboard events on the chip
   */
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      this.remove();
    }
  }

  /**
   * Allows for programmatic focusing of the chip
   */
  focus(): void {
    if (!this.disabled) {
      // If `focus` is called before `ngAfterViewInit`, we won't have access to the primary action.
      // This can happen if the consumer tries to focus a chip immediately after it is added.
      // Queue the method to be called again on init.
      if (this.primaryAction) {
        this.primaryAction.focus();
      } else {
        this.pendingFocus = true;
      }
    }
  }

  /**
   * Gets the action that contains a specific target node
   */
  getSourceAction(target: Node): ChipActionDirective | undefined {
    return this.getActions().find((action) => {
      const element = action.elementRef.nativeElement;

      return element === target || element.contains(target);
    });
  }

  /**
   * Gets all of the actions within the chip
   */
  getActions(): ChipActionDirective[] {
    const result: ChipActionDirective[] = [];

    if (this.primaryAction) {
      result.push(this.primaryAction);
    }

    if (this.removeIcon) {
      result.push(this.removeIcon);
    }

    return result;
  }

  /**
   * Handles interactions with the primary action of the chip
   */
  handlePrimaryActionInteraction(): void {
    // Empty here, but is overwritten in child classes.
  }

  /**
   * Gets the tabindex of the chip
   */
  getTabIndex(): Nullable<number> {
    if (!this.role) {
      return null;
    }

    return this.disabled ? -1 : this.tabIndex;
  }

  /**
   * Starts the focus monitoring process on the chip
   */
  private monitorFocus(): void {
    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(
        concatMap((origin) => {
          const hasFocus = origin !== null;

          if (hasFocus !== this.hasFocusInternal) {
            this.hasFocusInternal = hasFocus;

            if (hasFocus) {
              this.onFocus$.next({ chip: this });

              return this.onFocus$.asObservable();
            }

            // When animations are enabled, Angular may end up removing the chip from the DOM a little
            // earlier than usual, causing it to be blurred and throwing off the logic in the chip list
            // that moves focus not the next item. To work around the issue, we defer marking the chip
            // as not focused until the next time the zone stabilizes.
            return this.ngZone.onStable.pipe(
              take(1),
              tap(() => this.ngZone.run(() => this.onBlur$.next({ chip: this }))),
            );
          }

          return EMPTY;
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.elementRef);
    this.destroyed.emit({ chip: this });
    this.destroyed.complete();
  }
}
